/**
 * PendingGdprDeletions widget — admin glance at workers with an
 * in-flight account deletion request (S7 §2.2).
 *
 * Workers self-request deletion via /v1/me/account-deletion-request,
 * which sets ``Worker.deletion_requested_at = now()``. The retention
 * purge cron picks up rows where ``deletion_requested_at + 30 days
 * <= now()`` and hard-anonymizes them. Admins use this widget during
 * the 30-day grace to triage / cancel mistaken requests.
 *
 * Per row:
 *   - Name + worker_code
 *   - Days remaining in the grace (cron cutoff = requested_at + 30 days)
 *   - Cancel button — clears deletion_requested_at, removing the row
 *
 * Empty state intentionally renders the widget with a "no requests"
 * note rather than hiding the widget so admins know the surface is
 * live.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, IconButton, List,
  ListItem, Snackbar, Stack, Typography,
} from '@mui/material';
import { CancelOutlined, TaskAltOutlined } from '@mui/icons-material';
import { ApiError } from '../../../../api';
import { workersApi, type WorkerResponse } from '../../../../api/organization';
import { translateApiError } from '../../../../i18n/translateApiError';
import WidgetSummaryLine from './WidgetSummaryLine';
import WidgetEmptyState from '../../../../components/primitives/WidgetEmptyState';

const GRACE_DAYS = 30;

function daysRemaining(deletionRequestedAt: string | null | undefined): number {
  if (!deletionRequestedAt) return GRACE_DAYS;
  const requested = new Date(deletionRequestedAt).getTime();
  const cutoff = requested + GRACE_DAYS * 24 * 3_600_000;
  const remaining = Math.ceil((cutoff - Date.now()) / (24 * 3_600_000));
  return Math.max(0, remaining);
}

function urgencyColor(daysLeft: number): 'error' | 'warning' | 'info' | 'default' {
  if (daysLeft <= 3) return 'error';
  if (daysLeft <= 7) return 'warning';
  if (daysLeft <= 14) return 'info';
  return 'default';
}

const PendingGdprDeletions: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<WorkerResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<WorkerResponse | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await workersApi.gdpr.listPendingDeletions();
      setRows(data);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('dashboard:widgets.pendingGdpr.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCancel = async () => {
    if (!confirmTarget) return;
    setCancelling(true);
    try {
      await workersApi.gdpr.cancelDeletion(confirmTarget.id);
      setRows((prev) => prev.filter((r) => r.id !== confirmTarget.id));
      setToast(t('dashboard:widgets.pendingGdprDeletions.cancelToast', { name: `${confirmTarget.first_name} ${confirmTarget.last_name}`.trim() || t('dashboard:widgets.pendingGdpr.unknownWorker') }));
      setConfirmTarget(null);
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('dashboard:pendingGdpr.cancelFailed'));
    } finally {
      setCancelling(false);
    }
  };

  return (
    // No Paper and no title of its own. Dashboard.tsx already wraps every widget
    // in a bordered Paper and renders its title from one shared bar, so this had a
    // second border, a second title, and content at 16 + 16 = 32px — the "card
    // inside a card" svdy-ui §15 bans, and the worst-offending of the nineteen.
    // The count Chip survives, as the summary line's action.
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <WidgetSummaryLine
        action={rows.length > 0 ? (
          <Chip
            label={rows.length}
            size="small"
            color="warning"
            sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1, fontWeight: 600 }}
          />
        ) : undefined}
      >
        {t('dashboard:widgets.pendingGdpr.summary', { count: rows.length })}
      </WidgetSummaryLine>

      {loading ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
      ) : rows.length === 0 ? (
        <Box sx={{ flex: 1 }}>
          <WidgetEmptyState icon={TaskAltOutlined} title={t('dashboard:widgets.pendingGdpr.noPending')} />
        </Box>
      ) : (
        <List dense disablePadding sx={{ flex: 1, overflow: 'auto' }}>
          {rows.map((w) => {
            const daysLeft = daysRemaining(w.deletion_requested_at);
            return (
              <ListItem
                key={w.id}
                disableGutters
                sx={{
                  py: 0.75,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 0 },
                }}
                secondaryAction={
                  <IconButton
                    size="small"
                    onClick={() => setConfirmTarget(w)}
                    sx={{ p: 0.5 }}
                    title={t('dashboard:widgets.pendingGdprDeletions.cancelDeletion')}
                  >
                    <CancelOutlined sx={{ fontSize: 16 }} />
                  </IconButton>
                }
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    {w.first_name} {w.last_name}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      {w.worker_code}
                    </Typography>
                    <Chip
                      label={t('dashboard:widgets.pendingGdpr.daysLeft', { count: daysLeft })}
                      size="small"
                      color={urgencyColor(daysLeft)}
                      sx={{ height: 16, fontSize: '0.6rem', borderRadius: 0.75 }}
                    />
                  </Stack>
                </Box>
              </ListItem>
            );
          })}
        </List>
      )}

      {/* Confirm cancel dialog */}
      <Dialog
        open={!!confirmTarget}
        onClose={() => !cancelling && setConfirmTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ py: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
            {t('dashboard:widgets.pendingGdpr.confirmTitle')}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <DialogContentText sx={{ fontSize: '0.875rem' }}>
            {t('dashboard:widgets.pendingGdpr.confirmBody', {
              name: `${confirmTarget?.first_name ?? ''} ${confirmTarget?.last_name ?? ''}`.trim()
                || t('dashboard:widgets.pendingGdpr.unknownWorker'),
              code: confirmTarget?.worker_code ?? '—',
              count: GRACE_DAYS,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button
            onClick={() => setConfirmTarget(null)}
            disabled={cancelling}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {t('dashboard:widgets.pendingGdpr.keepRequest')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCancel}
            disabled={cancelling}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {cancelling ? <CircularProgress size={16} /> : t('dashboard:widgets.pendingGdpr.cancelDeletion')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PendingGdprDeletions;
